import { BackButton, ComponentL, ComponentS, ComponentTextStyle, LoadingIndicator, SearchBar, Size } from "@laerdal/life-react-components";
import axios from "axios";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDebouncedValue } from "rooks";

import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import ProductListItem from "../../commonComponents/ProductListItem";
import ProductApi from "../../services/api/ProductApi";
import SearchApi from "../../services/api/SearchApi";
import { PopularProductDto } from "../../model/dto/landingSearchResponseDto";
import { AssetListItemDto } from "../../model/dto/assetListItemDto";
import { buildMarketPath, selectMarket } from "../../store/slices/globalConfigSlice";
import { selectIsAuthenticated, signInWithAuth0 } from "../../store/slices/userSlice";
import CategoriesBrowser from "./components/CategoriesBrowser";
import {
  PageContainer,
  Header,
  Title,
  Subtitle,
  Banner,
  BannerText,
  BannerSignInButton,
  SearchSection,
  SectionTitle,
  ResultsBlock,
  ProductsGrid,
  LinkRow,
  LinkButton,
} from "./components/CaseAssetsSelectPageComponents";

const resultsPerPage = 6;

const CaseAssetSelectPage = () => {
  const { option: optionParam } = useParams<{ option: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("cases");
  const navigate = useNavigate();
  const currentMarket = useSelector(selectMarket);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const option = optionParam || "technical";
  const suboption = searchParams.get("suboption") || "technicalSupport";

  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get("search") || "");
  const [searchTermDebounced] = useDebouncedValue(searchTerm, 400);

  const [productsLoading, setProductsLoading] = useState<boolean>(true);
  const [assetsLoading, setAssetsLoading] = useState<boolean>(false);
  const [products, setProducts] = useState<PopularProductDto[]>([]);
  const [assets, setAssets] = useState<AssetListItemDto[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [assetNavigating, setAssetNavigating] = useState<string | null>(null);

  const buildInfoUrl = (
    productName?: string,
    productLineCode?: string,
    categoryLink?: string,
    productLink?: string,
    redirectUrl?: string
  ) => {
    const basePath = buildMarketPath(currentMarket, "/case/create/technical/assetSelect/info");
    const params = new URLSearchParams();
    params.set("suboption", suboption);
    if (productName) {
      params.set("productName", productName);
    }
    const resolvedProductLineCode = (productLineCode || productLink || "").toLowerCase();
    if (resolvedProductLineCode) {
      params.set("productLineCode", resolvedProductLineCode);
    }
    if (categoryLink) {
      params.set("categoryLink", categoryLink);
    }
    if (redirectUrl) {
      params.set("redirectUrl", redirectUrl);
    }
    return `${basePath}?${params.toString()}`;
  };

  const handleProductSelect = (
    productName: string,
    productLineCode?: string,
    categoryLink?: string,
    productLink?: string,
    redirectUrl?: string
  ) => {
    if (redirectUrl) {
      const params = new URLSearchParams();
      if (productName) params.set("productName", productName);
      const resolvedProductLineCode = (productLineCode || productLink || "").toLowerCase();
      if (resolvedProductLineCode) params.set("productLineCode", resolvedProductLineCode);
      navigate(`${buildMarketPath(currentMarket, `/case/create/${option}/${suboption}`)}?${params.toString()}`);
      return;
    }
    navigate(buildInfoUrl(productName, productLineCode, categoryLink, productLink, redirectUrl));
  };

  const handleAssetSelect = (asset: AssetListItemDto) => {
    
    const productLineName = asset.productLineName || "";
    const serialNumber = asset.serialNumber || "";

    posthog?.capture?.("SearchProductClick", {
      productLineNAme: productLineName,
      searchType: "caseCreation",
      searchTerm: searchTermDebounced,
      isAsset: true
    });
    
    posthog?.capture('SUPP-Casedeflection-AssetSelected', {
      productLineNAme: productLineName,
    });

    const params = new URLSearchParams();
    params.set("suboption", suboption);
    params.set("productName", productLineName);
    params.set("productLineName", productLineName);
    if (serialNumber) params.set("productSerial", serialNumber);
    const basePath = buildMarketPath(currentMarket, "/case/create/technical/assetSelect/info");
    navigate(`${basePath}?${params.toString()}`);
  };

  const handleSignIn = () => {
    posthog?.capture?.("CaseAssetSignIn");
    signInWithAuth0(window.location.href);
  };

  const buildCaseUrl = () => {
    return buildMarketPath(currentMarket, `/case/create/${option}/${suboption}`);
  };

  

  // Always fetch products (popular / search)
  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    setProductsLoading(true);

    SearchApi.ProductSearch(
      searchTermDebounced || null,
      currentPage - 1,
      resultsPerPage,
      undefined,
      controller.signal
    )
      .then((data) => {
        if (!isActive || !data) return;
        setProducts(data.products || []);
        const nextPageCount = Math.max(1, Math.ceil((data.totalCount || 0) / resultsPerPage));
        setPageCount(nextPageCount);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        throw error;
      })
      .finally(() => {
        if (!isActive) return;
        setProductsLoading(false);
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [searchTermDebounced, currentPage]);

  // Fetch assets when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setAssets([]);
      setAssetsLoading(false);
      return;
    }

    let isActive = true;
    setAssetsLoading(true);

    SearchApi.AssetSearch({
      search: searchTermDebounced || undefined,
      page: 0,
      pageSize: resultsPerPage,
    })
      .then((data) => {
        if (!isActive || !data) return;
        setAssets(data.items || []);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        if (isActive) {
          setAssets([]);
        }
      })
      .finally(() => {
        if (!isActive) return;
        setAssetsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [searchTermDebounced, isAuthenticated]);

  useEffect(() => {
    if (productsLoading) return;
    if (products.length === 0) return;
    posthog?.capture?.("SearchProductImpression", {
      productLines: products.map((product) => product.linkName || product.title),
      searchType: "caseCreation",
      searchTerm: searchTermDebounced
    });
  }, [productsLoading, products, searchTermDebounced]);

  useEffect(() => {
    if (assetsLoading || !isAuthenticated) return;
    if (assets.length === 0) return;
    posthog?.capture?.("SearchProductImpression", {
      productLines: assets.map((a) => a.productLineName || a.name || ""),
      searchType: "caseCreation",
      searchTerm: searchTermDebounced,
      isAsset: true
    });
  }, [assetsLoading, assets, searchTermDebounced, isAuthenticated]);

  useEffect(() => {
    if (productsLoading || (isAuthenticated && assetsLoading)) return;
    if (!searchTermDebounced || searchTermDebounced.trim().length === 0) return;
    const anyResults = products.length > 0 || (isAuthenticated && assets.length > 0);
    if (anyResults) return;

    posthog?.capture?.("SearchProductNoResult", {
      searchType: "caseCreation",
      searchTerm: searchTermDebounced
    });
  }, [productsLoading, assetsLoading, products.length, assets.length, searchTermDebounced, isAuthenticated]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTermDebounced]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (searchTermDebounced) {
        next.set("search", searchTermDebounced);
      } else {
        next.delete("search");
      }
      return next;
    }, { replace: true });
  }, [searchTermDebounced, setSearchParams]);

  const hasResults = products.length > 0 || (isAuthenticated && assets.length > 0);

  return (
    <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
      <PageContainer>
        <Header>
          <BackButton size={Size.Small} onClick={() => window.history.back()}>
            {t("Back")}
          </BackButton>
          <Title>{t("Select Product or Services")}</Title>
          <Subtitle textStyle={ComponentTextStyle.Regular}>
            {t("Choose an option below to access the support you need.")}
          </Subtitle>
        </Header>

        {!isAuthenticated && (
          <Banner>
            <BannerText>
              {t("Sign in with your Laerdal Account to access your products, warranty, software updates, and more!")}
            </BannerText>
            <BannerSignInButton type="button" onClick={handleSignIn}>
              {t("Sign-in")}
            </BannerSignInButton>
          </Banner>
        )}

        <SearchSection>
          <SearchBar
            id="CaseAssetSearch"
            placeholder={
              isAuthenticated
                ? t("Search by product name or serial number")
                : t("Search by product name")
            }
            searchTerm={searchTerm}
            enterSearch={() => { }}
            setSearchTerm={(value) => setSearchTerm(value)}
            removeSearch={() => {
              setSearchTerm("");
              setCurrentPage(1);
              const input = document.getElementById("CaseAssetSearch");
              if (input) {
                input.focus();
              }
            }}
            size={Size.Small}
          />

          {isAuthenticated && (
            <>
              <SectionTitle>{ t("My Products")}</SectionTitle>
              <ResultsBlock>
                {assetsLoading ? (
                  <LoadingIndicator />
                ) : (
                  <>
                    {assets?.length > 0 ? (
                      <ProductsGrid>
                        {assets.map((asset, index) => (
                          <ProductListItem
                            key={`${asset.id}-${index}`}
                            title={asset.name ?? asset.localizedName ?? asset.skuName ?? "Product"}
                            imageUrl={`https://laerdal.com/cdn-cgi/image/format=avif,quality=100,height=160,fit=contain/images/sku/${asset.sku}.png?lang=en`}
                            titleSize={16}
                            titleWeight={400}
                            onClick={() => handleAssetSelect(asset)}
                          />
                        ))}
                      </ProductsGrid>
                    ) : (
                        <>{t("No registered products found.")}</>
                      
                    )}
                  </>
                )}
              </ResultsBlock>
            </>
          )}

          {(!isAuthenticated || (searchTermDebounced && searchTermDebounced.trim().length > 0)) && (
            <>
              <SectionTitle>{(searchTermDebounced && searchTermDebounced.trim().length > 0) ? t("Search results") : t("Popular")}</SectionTitle>
              <ResultsBlock>
                {productsLoading ? (
                  <LoadingIndicator />
                ) : (
                  <>
                    {products?.length > 0 ? (
                      <ProductsGrid>
                        {products.map((product, index) => (
                          <ProductListItem
                            key={`${product.linkName || product.redirectUrl || product.title}-${index}`}
                            title={product.title}
                            imageUrl={product.imageUrl || "/assets/ProductPageDefault.png"}
                            titleSize={16}
                            titleWeight={400}
                            onClick={() => {
                              posthog?.capture?.("SearchProductClick", {
                                productLine: product.linkName || product.title,
                                searchType: "caseCreation",
                                searchTerm: searchTermDebounced
                              });
                              posthog?.capture('SUPP-Casedeflection-ProductSelected', {
                                productLine: product.linkName || product.title
                              });
                              
                              handleProductSelect(
                                product.title,
                                product.linkName,
                                product.categoryLink,
                                product.linkName,
                                product.redirectUrl
                              );
                            }}
                          />
                        ))}
                      </ProductsGrid>
                    ) : (
                      <ComponentS>{t("No results found. Try exploring the categories below.")}</ComponentS>
                    )}
                  </>
                )}
              </ResultsBlock>
            </>
          )}
        </SearchSection>

        {!searchTermDebounced && (
          <CategoriesBrowser
            searchTermDebounced={searchTermDebounced}
            onProductSelect={handleProductSelect}
          />
        )}

        <LinkRow>
          <LinkButton type="button" onClick={() => {
            posthog?.capture('SUPP-Casedeflection-SkipProduct');
            handleProductSelect("", undefined, undefined, undefined, undefined);
          }}>
            {t("Cannot specify - skip")}
          </LinkButton>
        </LinkRow>
      </PageContainer>
    </PageWidthFull>
  );
};

export default CaseAssetSelectPage;
